'use client'

import { useCallback, useEffect, useRef, useState } from 'react'
import type { MutableRefObject } from 'react'
import { extractYoutubeVideoId } from '@/lib/comment-embed-pipeline'
import { loadPlyrFromCdn, PLYR_CDN_ASSETS } from '@/lib/plyr-cdn'
import type { ShortsAudioControl } from '@/lib/shorts-audio-control'
import { registerShortsAudioControl } from '@/lib/shorts-audio-session'
import { getMediaFileUrl } from '@/lib/utils'
import { coalesceLegacyPlayerType, extractVimeoVideoId, VIDEO_PLAYER_TYPE } from '@/lib/video-utils'
import ShortYoutubePlayer from '@/components/shorts/ShortYoutubePlayer'
import styles from '@/components/shorts/shorts.module.css'

interface ShortVideoPlayerProps {
  video?: string | null
  youtube?: string | null
  dailymotion?: string | null
  vimeo?: string | null
  x?: string | null
  facebook?: string | null
  playerType?: number | null
  isActive: boolean
  shouldPreload?: boolean
  shouldWarm?: boolean
  playing: boolean
  muted: boolean
  volume?: number
  audioUnlocked?: boolean
  audioControlRef?: MutableRefObject<ShortsAudioControl | null>
  /** YouTube is rendered by the feed-level shared host. */
  useSharedYoutube?: boolean
  onTimeUpdate?: (current: number, duration: number) => void
  onPlayingChange?: (playing: boolean) => void
  onAudioChange?: (next: { muted: boolean; volume: number }) => void
}

export function resolveShortsYoutubeId(raw?: string | null): string | null {
  const value = (raw || '').trim()
  if (!value) return null
  return extractYoutubeVideoId(value) || (/^[a-zA-Z0-9_-]{11}$/.test(value) ? value : null)
}

type PlyrPlayer = {
  currentTime: number
  duration: number
  muted: boolean
  playing: boolean
  volume: number
  destroy: () => void
  pause: () => void
  play: () => Promise<void> | void
  on: (event: string, callback: () => void) => void
}

interface PlaybackSurfacePointer {
  pointerId: number
  startX: number
  startY: number
  moved: boolean
}

function clampVolume(value: number): number {
  return Math.min(100, Math.max(0, Number.isFinite(value) ? value : 100))
}

function buildPlyrOptions(
  provider: 'html5' | 'vimeo',
  startsWithAutoplay: boolean,
  initialVolume: number,
) {
  return {
    controls: ['progress'],
    settings: ['speed'],
    ratio: '9:16',
    autoplay: startsWithAutoplay,
    muted: startsWithAutoplay,
    volume: clampVolume(initialVolume) / 100,
    clickToPlay: false,
    hideControls: true,
    resetOnEnd: false,
    keyboard: { focused: true, global: false },
    tooltips: { controls: true, seek: true },
    displayDuration: true,
    invertTime: false,
    toggleInvert: false,
    disableContextMenu: true,
    loop: { active: true },
    fullscreen: { enabled: true, fallback: true, iosNative: false },
    loadSprite: true,
    iconUrl: PLYR_CDN_ASSETS.iconUrl,
    blankVideo: PLYR_CDN_ASSETS.blankVideo,
    vimeo:
      provider === 'vimeo'
        ? {
            byline: false,
            portrait: false,
            title: false,
            speed: true,
            transparent: false,
          }
        : undefined,
    i18n: {
      restart: 'Yeniden başlat',
      rewind: 'Geri sar {seektime}s',
      play: 'Oynat',
      pause: 'Duraklat',
      fastForward: 'İleri sar {seektime}s',
      seek: 'İlerle',
      seekLabel: '{currentTime} / {duration}',
      played: 'Oynatıldı',
      buffered: 'Önbellek',
      currentTime: 'Geçen süre',
      duration: 'Süre',
      volume: 'Ses',
      mute: 'Sesi kapat',
      unmute: 'Sesi aç',
      settings: 'Ayarlar',
      quality: 'Kalite',
      speed: 'Hız',
      normal: 'Normal',
      enterFullscreen: 'Tam ekran',
      exitFullscreen: 'Tam ekrandan çık',
    },
  }
}

export default function ShortVideoPlayer(props: ShortVideoPlayerProps) {
  const resolvedType = coalesceLegacyPlayerType(
    props.playerType,
    props.youtube,
    props.dailymotion,
    props.video,
    props.vimeo,
    props.x,
    props.facebook,
  )
  const youtubeId =
    resolvedType === VIDEO_PLAYER_TYPE.YOUTUBE
      ? resolveShortsYoutubeId(props.youtube)
      : null

  if (youtubeId) {
    if (props.useSharedYoutube) {
      return <div className={styles.mediaLayer} data-shorts-shared-youtube="" />
    }
    return (
      <ShortYoutubePlayer
        youtubeId={youtubeId}
        isActive={props.isActive}
        shouldPreload={props.shouldPreload}
        shouldWarm={props.shouldWarm}
        playing={props.playing}
        muted={props.muted}
        volume={props.volume}
        audioUnlocked={props.audioUnlocked}
        audioControlRef={props.audioControlRef}
        onTimeUpdate={props.onTimeUpdate}
        onPlayingChange={props.onPlayingChange}
      />
    )
  }

  return <ShortPlyrVideoPlayer {...props} />
}

function ShortPlyrVideoPlayer({
  video,
  dailymotion,
  vimeo,
  x,
  facebook,
  playerType,
  isActive,
  shouldPreload = false,
  shouldWarm = false,
  playing,
  muted,
  volume = 50,
  audioUnlocked = false,
  audioControlRef,
  onTimeUpdate,
  onPlayingChange,
}: ShortVideoPlayerProps) {
  const mountRef = useRef<HTMLDivElement>(null)
  const playerRef = useRef<PlyrPlayer | null>(null)
  const isActiveRef = useRef(isActive)
  const playingRef = useRef(playing)
  const mutedRef = useRef(muted)
  const volumeRef = useRef(clampVolume(volume))
  const audioUnlockedRef = useRef(audioUnlocked)
  const shouldWarmRef = useRef(shouldWarm)
  const hasStartedPlaybackRef = useRef(false)
  const hasPlaybackProgressRef = useRef(false)
  const hasActivePlaybackProgressRef = useRef(false)
  const preloadWarmedRef = useRef(false)
  const playerApiReadyRef = useRef(false)
  const playWatchdogRef = useRef<number | undefined>(undefined)
  const deferredPauseRef = useRef<number | undefined>(undefined)
  const playbackSurfacePointerRef = useRef<PlaybackSurfacePointer | null>(null)
  const suppressPlaybackSurfaceClickUntilRef = useRef(0)
  const callbacksRef = useRef({
    onTimeUpdate,
    onPlayingChange,
  })
  const [status, setStatus] = useState<'idle' | 'loading' | 'ready' | 'error'>('idle')

  const resolvedType = coalesceLegacyPlayerType(
    playerType,
    undefined,
    dailymotion,
    video,
    vimeo,
    x,
    facebook,
  )
  const vimeoId =
    resolvedType === VIDEO_PLAYER_TYPE.VIMEO && vimeo ? extractVimeoVideoId(vimeo) : null
  const videoUrl =
    resolvedType === VIDEO_PLAYER_TYPE.MP4 && video?.trim() ? getMediaFileUrl(video) : null
  const provider = vimeoId ? 'vimeo' : videoUrl ? 'html5' : null
  const mediaKey = vimeoId
    ? `vimeo:${vimeoId}`
    : videoUrl
      ? `video:${videoUrl}`
      : `unsupported:${resolvedType}`
  const shouldKeepPlayer = isActive || shouldPreload

  useEffect(() => {
    const becameActive = isActive && !isActiveRef.current
    if (becameActive) {
      hasActivePlaybackProgressRef.current = false
      suppressPlaybackSurfaceClickUntilRef.current = Date.now() + 700
    } else if (!isActive) {
      hasActivePlaybackProgressRef.current = false
    }
    isActiveRef.current = isActive
    playingRef.current = playing
    mutedRef.current = muted
    volumeRef.current = clampVolume(volume)
    audioUnlockedRef.current = audioUnlocked
    shouldWarmRef.current = shouldWarm
  }, [audioUnlocked, isActive, muted, playing, shouldWarm, volume])

  useEffect(() => {
    if (isActive) {
      suppressPlaybackSurfaceClickUntilRef.current = Date.now() + 700
    }
  }, [isActive, mediaKey])

  useEffect(() => {
    callbacksRef.current = {
      onTimeUpdate,
      onPlayingChange,
    }
  }, [onPlayingChange, onTimeUpdate])

  const canUnmute = useCallback(() => {
    // Only an explicit unlock from the mute/volume controls may unmute.
    return audioUnlockedRef.current
  }, [])

  const applyAudio = useCallback(
    (player: PlyrPlayer) => {
      const nextVolume = volumeRef.current / 100
      const userWantsMute =
        mutedRef.current || nextVolume <= 0 || !canUnmute()
      // Keep unlocked players unmuted while inactive (volume 0) so swipe
      // reactivation does not need a fresh unMute() gesture.
      player.volume = isActiveRef.current && !userWantsMute ? nextVolume : 0
      player.muted = userWantsMute
    },
    [canUnmute],
  )

  useEffect(() => {
    const control: ShortsAudioControl = {
      applyUserAudio: (nextMuted, nextVolume) => {
        audioUnlockedRef.current = true
        mutedRef.current = nextMuted
        volumeRef.current = clampVolume(nextVolume)
        const player = playerRef.current
        if (!player) return
        applyAudio(player)
        if (!nextMuted && isActiveRef.current && playingRef.current) {
          void Promise.resolve(player.play()).catch(() => {})
        }
      },
      seekTo: (seconds) => {
        const player = playerRef.current
        if (!player || !isActiveRef.current) return
        const duration = Math.max(0, Number(player.duration) || 0)
        const nextTime = Math.min(
          duration || Number.MAX_SAFE_INTEGER,
          Math.max(0, Number(seconds) || 0),
        )
        try {
          player.currentTime = nextTime
          if (playingRef.current) void Promise.resolve(player.play()).catch(() => {})
        } catch {
          /* ignore */
        }
      },
      getPlaybackTime: () => {
        const player = playerRef.current
        if (!player) return { current: 0, duration: 0 }
        return {
          current: Math.max(0, Number(player.currentTime) || 0),
          duration: Math.max(0, Number(player.duration) || 0),
        }
      },
    }
    if (audioControlRef) audioControlRef.current = control
    const unregister = registerShortsAudioControl(control)
    return () => {
      unregister()
      if (audioControlRef?.current === control) audioControlRef.current = null
    }
  }, [applyAudio, audioControlRef])

  const pausePlayer = useCallback((player: PlyrPlayer) => {
    try {
      player.pause()
    } catch {
      /* The provider may already be disposing the iframe. */
    }
  }, [])

  const clearPlayWatchdog = useCallback(() => {
    if (playWatchdogRef.current) {
      window.clearTimeout(playWatchdogRef.current)
      playWatchdogRef.current = undefined
    }
  }, [])

  const clearDeferredPause = useCallback(() => {
    if (deferredPauseRef.current) {
      window.clearTimeout(deferredPauseRef.current)
      deferredPauseRef.current = undefined
    }
  }, [])

  const warmPreload = useCallback(
    (player: PlyrPlayer) => {
      if (
        playerRef.current !== player ||
        isActiveRef.current ||
        !shouldWarmRef.current ||
        preloadWarmedRef.current
      ) {
        return
      }

      applyAudio(player)
      preloadWarmedRef.current = true
      setStatus('ready')
    },
    [applyAudio],
  )

  const armPlayWatchdog = useCallback(
    (player: PlyrPlayer) => {
      clearPlayWatchdog()
      let retryCount = 0
      let lastObservedTime = Number(player.currentTime) || 0
      let advancingSamples = 0

      const verifyPlayback = () => {
        playWatchdogRef.current = undefined
        if (playerRef.current !== player || !isActiveRef.current || !playingRef.current) {
          return
        }

        const currentTime = Number(player.currentTime) || 0
        if (currentTime > lastObservedTime + 0.08) {
          advancingSamples += 1
          lastObservedTime = currentTime
          if (advancingSamples >= 2) {
            hasActivePlaybackProgressRef.current = true
            applyAudio(player)
            return
          }
          playWatchdogRef.current = window.setTimeout(verifyPlayback, 300)
          return
        }

        if (retryCount < 2) {
          retryCount += 1
          advancingSamples = 0
          lastObservedTime = currentTime
          if (player.playing) {
            // Buffering sırasında yeniden play() göndermek YouTube'un kendi
            // merkez pause bezeline yol açabiliyor. Sağlayıcı hâlâ playing
            // raporluyorsa yalnızca ilerlemeyi tekrar ölç.
            playWatchdogRef.current = window.setTimeout(verifyPlayback, 700)
            return
          }
          // YouTube can report "playing" while its iframe is still parked on
          // a preloaded frame. A muted retry is autoplay-safe; configured audio
          // is restored as soon as currentTime actually advances.
          player.volume = volumeRef.current / 100
          player.muted = true
          try {
            const retry = player.play()
            void Promise.resolve(retry).catch(() => {})
          } catch {
            /* Final watchdog pass will expose the normal play affordance. */
          }
          playWatchdogRef.current = window.setTimeout(verifyPlayback, 700)
          return
        }

        // Yavaş bağlantıda YouTube currentTime'ı birkaç saniye sabit
        // tutabilir. Burada intent'i false yapıp pause göndermek, başlayan
        // videoyu 0.x saniyede durduruyor ve ortadaki provider düğmesini
        // görünür bırakıyordu. İki güvenli retry sonrasında sağlayıcının
        // buffer akışına müdahale etme; kullanıcı niyeti oynat olarak kalsın.
      }

      playWatchdogRef.current = window.setTimeout(verifyPlayback, 450)
    },
    [applyAudio, clearPlayWatchdog],
  )

  const requestPlay = useCallback(
    async (player: PlyrPlayer, _userInitiated = false) => {
      if (!isActiveRef.current || !playingRef.current) return

      applyAudio(player)
      try {
        await Promise.resolve(player.play())
        armPlayWatchdog(player)
      } catch {
        playingRef.current = false
        callbacksRef.current.onPlayingChange?.(false)
      }
    },
    [applyAudio, armPlayWatchdog],
  )

  const syncPlayback = useCallback(() => {
    const player = playerRef.current
    if (!player) return
    if (!isActiveRef.current || !playingRef.current) {
      clearPlayWatchdog()
      applyAudio(player)
      if (!isActiveRef.current && shouldWarmRef.current) {
        if (!preloadWarmedRef.current) {
          warmPreload(player)
        }
        return
      }
      clearDeferredPause()
      if (document.hidden) {
        pausePlayer(player)
        return
      }
      if (isActiveRef.current) {
        pausePlayer(player)
        return
      }
      deferredPauseRef.current = window.setTimeout(() => {
        deferredPauseRef.current = undefined
        if (playerRef.current === player && !isActiveRef.current) pausePlayer(player)
      }, 420)
      return
    }
    clearDeferredPause()
    if (player.playing && hasStartedPlaybackRef.current) {
      applyAudio(player)
      armPlayWatchdog(player)
      return
    }
    void requestPlay(player)
  }, [
    applyAudio,
    armPlayWatchdog,
    clearDeferredPause,
    clearPlayWatchdog,
    pausePlayer,
    requestPlay,
    warmPreload,
  ])

  const togglePlayback = useCallback(() => {
    const player = playerRef.current
    if (!player || !isActiveRef.current) return

    if (
      playingRef.current &&
      (player.playing ||
        hasActivePlaybackProgressRef.current ||
        hasStartedPlaybackRef.current)
    ) {
      clearPlayWatchdog()
      playingRef.current = false
      pausePlayer(player)
      callbacksRef.current.onPlayingChange?.(false)
      return
    }

    playingRef.current = true
    callbacksRef.current.onPlayingChange?.(true)
    void requestPlay(player, true)
  }, [clearPlayWatchdog, pausePlayer, requestPlay])

  useEffect(() => {
    syncPlayback()
  }, [audioUnlocked, isActive, muted, playing, shouldWarm, syncPlayback, volume])

  useEffect(() => {
    const handleVisibilityChange = () => {
      const player = playerRef.current
      if (!player) return
      if (document.hidden) {
        clearPlayWatchdog()
        pausePlayer(player)
        return
      }
      syncPlayback()
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [clearPlayWatchdog, pausePlayer, syncPlayback])

  useEffect(() => {
    const root = mountRef.current
    if (!root || !shouldKeepPlayer || !provider) {
      return
    }

    let cancelled = false
    hasStartedPlaybackRef.current = false
    hasPlaybackProgressRef.current = false
    hasActivePlaybackProgressRef.current = false
    preloadWarmedRef.current = false
    playerApiReadyRef.current = false
    clearPlayWatchdog()
    queueMicrotask(() => {
      if (!cancelled) {
        setStatus('loading')
      }
    })

    const destroy = () => {
      clearPlayWatchdog()
      clearDeferredPause()
      const player = playerRef.current
      if (player && playerApiReadyRef.current) {
        try {
          pausePlayer(player)
          player.destroy()
        } catch {
          /* The provider may already have removed its iframe. */
        }
      }
      playerRef.current = null
      playerApiReadyRef.current = false
      root.replaceChildren()
    }

    destroy()

    void loadPlyrFromCdn()
      .then((Plyr) => {
        if (cancelled || !mountRef.current) return

        const target =
          provider === 'html5' ? document.createElement('video') : document.createElement('div')

        target.id = `short-plyr-${mediaKey.replace(/[^a-zA-Z0-9_-]/g, '-')}`
        if (provider === 'vimeo') {
          target.setAttribute('data-plyr-provider', 'vimeo')
          target.setAttribute('data-plyr-embed-id', vimeoId!)
        } else {
          const videoElement = target as HTMLVideoElement
          videoElement.controls = true
          videoElement.loop = true
          videoElement.muted = true
          videoElement.playsInline = true
          videoElement.preload = 'auto'
          videoElement.setAttribute('playsinline', '')
          videoElement.setAttribute('webkit-playsinline', '')
          const source = document.createElement('source')
          source.src = videoUrl!
          source.type = videoUrl!.toLowerCase().includes('.webm') ? 'video/webm' : 'video/mp4'
          videoElement.appendChild(source)
        }

        root.replaceChildren(target)
        const startsWithAutoplay =
          !document.hidden && isActiveRef.current && playingRef.current
        const player = new Plyr(
          target,
          buildPlyrOptions(provider, startsWithAutoplay, volumeRef.current),
        ) as unknown as PlyrPlayer
        playerRef.current = player
        let playbackInitialized = false
        const initializePlayback = () => {
          if (cancelled) return
          if (playbackInitialized) return
          playbackInitialized = true
          playerApiReadyRef.current = true
          setStatus('ready')
          if (!startsWithAutoplay) {
            syncPlayback()
            return
          }

          applyAudio(player)
          if (isActiveRef.current && playingRef.current) {
            armPlayWatchdog(player)
          }
        }
        const reportProgress = () => {
          if (cancelled) return
          const currentTime = Number(player.currentTime) || 0
          if (!hasPlaybackProgressRef.current && currentTime > 0.05) {
            hasPlaybackProgressRef.current = true
            hasStartedPlaybackRef.current = true
          }
          callbacksRef.current.onTimeUpdate?.(currentTime, Number(player.duration) || 0)
        }
        player.on('ready', initializePlayback)
        player.on('canplay', () => {
          if (!cancelled) setStatus('ready')
        })
        player.on('playing', () => {
          if (cancelled) return
          if (!isActiveRef.current) {
            if (shouldWarmRef.current && playingRef.current) {
              hasStartedPlaybackRef.current = true
              return
            }
            pausePlayer(player)
            return
          }
          if (!playingRef.current) {
            pausePlayer(player)
            return
          }
          hasStartedPlaybackRef.current = true
          callbacksRef.current.onPlayingChange?.(true)
          if (isActiveRef.current && playingRef.current) {
            armPlayWatchdog(player)
          }
        })
        player.on('pause', () => {
          if (
            cancelled ||
            !hasStartedPlaybackRef.current ||
            !isActiveRef.current ||
            document.hidden
          ) {
            return
          }
          if (!playingRef.current) {
            callbacksRef.current.onPlayingChange?.(false)
            return
          }
          hasActivePlaybackProgressRef.current = false
          window.setTimeout(() => {
            if (!cancelled && isActiveRef.current && playingRef.current) {
              syncPlayback()
            }
          }, 120)
        })
        player.on('ended', () => {
          if (!cancelled) callbacksRef.current.onPlayingChange?.(false)
        })
        player.on('timeupdate', reportProgress)
        player.on('seeked', reportProgress)
        player.on('error', () => {
          if (!cancelled) setStatus('error')
        })

        window.setTimeout(() => {
          if (!cancelled && playerRef.current === player) {
            setStatus((current) => (current === 'loading' ? 'ready' : current))
          }
        }, 6000)
      })
      .catch(() => {
        if (!cancelled) setStatus('error')
      })

    return () => {
      cancelled = true
      destroy()
    }
  }, [
    applyAudio,
    armPlayWatchdog,
    clearDeferredPause,
    clearPlayWatchdog,
    mediaKey,
    pausePlayer,
    provider,
    requestPlay,
    shouldKeepPlayer,
    syncPlayback,
    videoUrl,
    vimeoId,
  ])

  if (!provider) {
    return (
      <div
        className={`${styles.mediaLayer} flex items-center justify-center px-6 text-center text-sm text-white/70`}
      >
        Video kaynağı bulunamadı
      </div>
    )
  }

  return (
    <div
      className={`${styles.mediaLayer} ${styles.shortPlyrHost}`}
      data-shorts-player={shouldKeepPlayer ? '' : undefined}
      data-shorts-provider={shouldKeepPlayer ? provider : undefined}
    >
      <div ref={mountRef} className={styles.shortPlyrMount} />
      {shouldKeepPlayer && isActive ? (
        <div
          className={styles.playbackSurface}
          aria-label={playing ? 'Videoyu durdur' : 'Videoyu oynat'}
          tabIndex={0}
          onPointerDown={(event) => {
            playbackSurfacePointerRef.current = {
              pointerId: event.pointerId,
              startX: event.clientX,
              startY: event.clientY,
              moved: false,
            }
          }}
          onPointerMove={(event) => {
            const pointer = playbackSurfacePointerRef.current
            if (!pointer || pointer.pointerId !== event.pointerId) return
            if (
              Math.abs(event.clientX - pointer.startX) > 10 ||
              Math.abs(event.clientY - pointer.startY) > 10
            ) {
              pointer.moved = true
            }
          }}
          onPointerUp={(event) => {
            const pointer = playbackSurfacePointerRef.current
            if (!pointer || pointer.pointerId !== event.pointerId) return
            if (pointer.moved) {
              suppressPlaybackSurfaceClickUntilRef.current = Date.now() + 400
            }
            playbackSurfacePointerRef.current = null
          }}
          onPointerCancel={(event) => {
            const pointer = playbackSurfacePointerRef.current
            if (!pointer || pointer.pointerId !== event.pointerId) return
            if (pointer.moved) {
              suppressPlaybackSurfaceClickUntilRef.current = Date.now() + 400
            }
            playbackSurfacePointerRef.current = null
          }}
          onClick={(event) => {
            if (Date.now() < suppressPlaybackSurfaceClickUntilRef.current) {
              event.preventDefault()
              return
            }
            togglePlayback()
          }}
          onKeyDown={(event) => {
            if (event.key !== 'Enter' && event.key !== ' ') return
            event.preventDefault()
            togglePlayback()
          }}
        />
      ) : null}
      {shouldKeepPlayer && status === 'error' ? (
        <div className={styles.shortPlyrError}>Video yüklenemedi</div>
      ) : null}
    </div>
  )
}
